import { useCallback, useEffect, useState } from 'react';

import { UpdatesRefreshEvent, UpdatesRefreshEventType, UpdatesRefreshPayload } from '@vtblife/event-bus-events';
import { EventBus } from '@vtblife/event-bus';
import { UpdatesData, UpdatingServices } from '@vtblife/layout-config/types';

import { reportErrorToSentry } from '../utils';
import { updatesApiService } from '../../services/updates-api';
import { useAuthorizeContext } from '../contexts';

const eventBus = EventBus.getInstance();

// Период поллинга-фолбэка для старых браузеров без поддержки BroadcastChannel
const UPDATES_POLL_INTERVAL_MS = 20 * 1000;

// Получение апдейтов для сервисов
// https://jira.m2.ru/browse/LK-4730
export const useUpdates = () => {
    const { user, isAuthorized } = useAuthorizeContext();
    const [servicesUpdates, setServicesUpdates] = useState<UpdatesData>({});
    const refresh = useCallback((services: UpdatingServices[]) => {
        const event: UpdatesRefreshEvent = {
            type: 'updates:refresh',
            category: 'simple',
            data: {
                services,
            },
        };
        eventBus.publish(event);
    }, []);

    useEffect(() => {
        if (!isAuthorized) return;

        let abortController: AbortController | undefined;
        const broadcastChannel = typeof BroadcastChannel === 'undefined' ? undefined : new BroadcastChannel('updates');

        const refreshData = async (services?: UpdatingServices[]) => {
            abortController?.abort();
            const controller = typeof AbortController !== 'undefined' ? new AbortController() : undefined;
            abortController = controller;

            try {
                const { data } = await updatesApiService.getUpdates(services, controller?.signal);
                setServicesUpdates((servicesUpdates) => ({ ...servicesUpdates, ...data }));
            } catch (error) {
                if (controller?.signal.aborted) return;
                reportErrorToSentry({ error });
            }
        };

        refreshData();

        const sub = eventBus.subscribe<UpdatesRefreshEventType, UpdatesRefreshPayload>(
            'updates:refresh',
            ({ data }) => {
                if (!isAuthorized) return;
                const { services } = data;
                refreshData(services as UpdatingServices[]);
                broadcastChannel?.postMessage({ services });
            },
        );

        // Синхронизация между вкладками через BroadcastChannel,
        // либо периодический поллинг как фолбэк для старых браузеров.
        const handleBroadcastChannelMessage = (event: MessageEvent<{ services: UpdatingServices[] }>) => {
            refreshData(event.data.services);
        };
        let pollIntervalId: ReturnType<typeof setInterval> | undefined;

        if (broadcastChannel) {
            broadcastChannel.addEventListener('message', handleBroadcastChannelMessage);
        } else {
            pollIntervalId = setInterval(() => refreshData(), UPDATES_POLL_INTERVAL_MS);
        }

        return () => {
            sub.unsubscribe();
            abortController?.abort();

            if (broadcastChannel) {
                broadcastChannel.removeEventListener('message', handleBroadcastChannelMessage);
                broadcastChannel.close();
            } else if (pollIntervalId) {
                clearInterval(pollIntervalId);
            }
        };
    }, [isAuthorized, user?.userId]);

    return { servicesUpdates, refresh };
};
